import { isInvalidDomainIdError } from '../domain/object/invalidDomainIdError';
import {
  ApplicationError,
  type ApplicationErrorCode,
  type ApplicationErrorLike,
  type ApplicationErrorResponse,
  toApplicationErrorResponse,
} from './applicationError';

const badRequestErrorDefinitions = {
  invalidId: {
    id: 'common.request.invalidId',
    httpStatus: 400,
    message: 'IDの形式が不正です',
  },
} as const satisfies Record<string, ApplicationErrorCode>;

type BadRequestErrorReason = keyof typeof badRequestErrorDefinitions;
export type BadRequestErrorCode = (typeof badRequestErrorDefinitions)[BadRequestErrorReason];
export type BadRequestErrorLike = ApplicationErrorLike<BadRequestErrorCode>;
export type BadRequestErrorResponse = ApplicationErrorResponse<BadRequestErrorCode>;

export class BadRequestError extends ApplicationError<BadRequestErrorCode> {
  static Name = 'BadRequestError';

  private constructor(reason: BadRequestErrorReason, options?: { cause?: unknown }) {
    super(badRequestErrorDefinitions[reason], undefined, options);
    this.name = BadRequestError.Name;
  }

  static InvalidId(options?: { cause?: unknown }): BadRequestError {
    return new BadRequestError('invalidId', options);
  }
}

const toRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'object' || value === null) return undefined;
  return value as Record<string, unknown>;
};

const badRequestErrorReasonsById = Object.fromEntries(
  Object.entries(badRequestErrorDefinitions).map(([reason, errorCode]) => [errorCode.id, reason]),
) as Record<BadRequestErrorCode['id'], BadRequestErrorReason>;

const getBadRequestErrorReason = (errorCodeId: unknown): BadRequestErrorReason | undefined => {
  if (typeof errorCodeId !== 'string') return undefined;
  return badRequestErrorReasonsById[errorCodeId as BadRequestErrorCode['id']];
};

export const isBadRequestError = (error: unknown): error is BadRequestErrorLike => {
  if (error instanceof BadRequestError) return true;
  const record = toRecord(error);
  const errorCode = toRecord(record?.errorCode);
  const reason = getBadRequestErrorReason(errorCode?.id);
  return (
    record?.name === BadRequestError.Name &&
    reason !== undefined &&
    errorCode?.httpStatus === badRequestErrorDefinitions[reason].httpStatus &&
    errorCode.message === badRequestErrorDefinitions[reason].message
  );
};

export const toBadRequestErrorResponse = (error: BadRequestErrorLike): BadRequestErrorResponse => toApplicationErrorResponse(error);

export const parseRequestId = <T>(value: string, parse: (value: string) => T): T => {
  try {
    return parse(value);
  } catch (error) {
    if (isInvalidDomainIdError(error)) throw BadRequestError.InvalidId({ cause: error });
    throw error;
  }
};
